import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight } from 'lucide-react';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<'register' | 'queue' | 'reports' | 'reviewDue' | 'maternity'>('register');
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  // Computed properties
  const activeEncounters = useMemo(() => {
    const now = Date.now();
    const upper = now + 24 * 60 * 60 * 1000;
    return encounters.filter(e => {
      if (!e.reviewDate) return false;
      if (e.reviewStatus === 'Cancelled') return false;
      const t = new Date(e.reviewDate).getTime();
      if (Number.isNaN(t)) return false;
      return t <= upper; // includes overdue (t < now) AND future-within-24h
    }).length;
  }, [encounters]);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
    setActiveTab('search');
    setSearchQuery(newPatient.bmcFileNumber);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
  };
  const handleAddToQueue = (patientId: string) => {
    createEncounter(patientId);
    alert('Patient added to queue successfully.');
  };
  return <div className="space-y-6" data-id="element-5840" data-mp="368aaiv83llh7">
      <div className="flex justify-between items-center" data-id="element-5841" data-mp="3678ojvpjnekp">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-5842" data-mp="3b13gtnj7t1ve">
          Reception Dashboard
        </h1>
        <nav className="flex space-x-2" data-id="element-5843" data-mp="zk1aoldlsf9i">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5844" data-mp="zizxobd11ip5">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5845" data-mp="14cb8enkymjmc" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5846" data-mp="gmgx4agah347">
            <Users className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5847" data-mp="ll1um245p8oy" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5848" data-mp="2110qadwl7c8b">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5849" data-mp="npcax6tqix0h" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5850" data-mp="ixu6g8si6ear">
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5851" data-mp="401ufqm6081b">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5852" data-mp="1ud983pvnpuc7" />
            Maternity Registration
          </button>
        </nav>
      </div>

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-5853" data-mp="1wxyqv5emjt2k">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-5854" data-mp="1wwxhd5d9bemy">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5855" data-mp="21ws5j15ndq4b">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-5856" data-mp="21uyxpijaszuq" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-5857" data-mp="1iy60xcax9gvn" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-5858" data-mp="2zd7d2v2irz27" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-5859" data-mp="z9oauyvexikj" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-5860" data-mp="pjlagdlyp2c6" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-5861" data-mp="24hpii3qdaj8m" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-5862" data-mp="2n7yweavinzg5" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-5863" data-mp="1r403icfoqggm" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-5864" data-mp="1d8xsxbzw82xh">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-5865" data-mp="2a09nthh6k392">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5866" data-mp="260he31g1vr24">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-5867" data-mp="20zjzszy5vh5w" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-5868" data-mp="kopo7204epdb" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-5869" data-mp="p843dvo4fopr" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5870" data-mp="18559jjtevuva">
              <Button type="submit" size="lg" data-id="element-5871" data-mp="34ygmszjw0v49">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <div className="space-y-6" data-id="element-5872" data-mp="31wvke50k79ab">
          <Card data-id="element-5873" data-mp="31xwykicvgbw9">
            <div className="relative" data-id="element-5874" data-mp="31xdqrugxr3vy">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5875" data-mp="2wxsj8vnp9n7m">
                <Search className="h-5 w-5 text-gray-400" data-id="element-5876" data-mp="3bv1kqi6pw7d2" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5877" data-mp="3bsyqtn2g1u32" />
            </div>
          </Card>

          {searchQuery && !selectedPatient && <Card noPadding data-id="element-5878" data-mp="7hv1wkvqnb14">
              <ul className="divide-y divide-gray-200" data-id="element-5879" data-mp="7h36ba255dau">
                {filteredPatients.length > 0 ? filteredPatients.map(patient => <li key={patient.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedPatient(patient)} data-id="element-5880" data-mp="m4lfolqnew3l">
                      <div data-id="element-5881" data-mp="d56t3xdlr5od">
                        <p className="text-sm font-medium text-blue-600" data-id="element-5882" data-mp="i1f91czmj2wl">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-5883" data-mp="3axzt7gqz8p5">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-5884" data-mp="3qirx8u1jph3t">
                          {patient.age} yrs • {patient.sex} • {patient.contact}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-5885" data-mp="3gklyumey6t3p" />
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-5886" data-mp="3lkqeybby1lte">
                    No patients found matching "{searchQuery}"
                  </li>}
              </ul>
            </Card>}

          {selectedPatient && <div className="space-y-6" data-id="element-5887" data-mp="3rz7f5qsi70q2">
              <Button variant="ghost" onClick={() => setSelectedPatient(null)} className="mb-2" data-id="element-5888" data-mp="3ry5rmmuawlrg">
                ← Back to search results
              </Button>

              <Card title="Patient Details" action={<Button onClick={() => handleAddToQueue(selectedPatient.id)} data-id="element-5890" data-mp="3d8zwpb2hxhpd">
                    Add to Queue
                  </Button>} data-id="element-5889" data-mp="3db2tcdy5m932">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-5891" data-mp="34bz8p5jc9lmg">
                  <div data-id="element-5892" data-mp="2za9vpkck2xvq">
                    <p className="text-sm text-gray-500" data-id="element-5893" data-mp="2zas39d4o4yiy">BMC Number</p>
                    <p className="font-medium" data-id="element-5894" data-mp="2ubyozihk53hq">
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                  <div data-id="element-5895" data-mp="19wae5rqs1wzb">
                    <p className="text-sm text-gray-500" data-id="element-5896" data-mp="1et0ri7b76eh1">Full Name</p>
                    <p className="font-medium" data-id="element-5897" data-mp="1evdgh4fdhp3l">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                  </div>
                  <div data-id="element-5898" data-mp="1nsnpsk8mr45v">
                    <p className="text-sm text-gray-500" data-id="element-5899" data-mp="1nruse9vk9r0e">Age / Sex</p>
                    <p className="font-medium" data-id="element-5900" data-mp="r4zk51pr9437">
                      {selectedPatient.age} / {selectedPatient.sex}
                    </p>
                  </div>
                  <div data-id="element-5901" data-mp="103usokg9uq9o">
                    <p className="text-sm text-gray-500" data-id="element-5902" data-mp="103bjpt8lnc7s">Contact</p>
                    <p className="font-medium" data-id="element-5903" data-mp="14uuqrt14nedr">{selectedPatient.contact}</p>
                  </div>
                  <div data-id="element-5904" data-mp="8dgiozbdpy3a">
                    <p className="text-sm text-gray-500" data-id="element-5905" data-mp="8c5hn79bj4fv">Address</p>
                    <p className="font-medium" data-id="element-5906" data-mp="cek03q92s26m">{selectedPatient.address}</p>
                  </div>
                  <div data-id="element-5907" data-mp="3fz06b3qm5opp">
                    <p className="text-sm text-gray-500" data-id="element-5908" data-mp="3fzjc5m2zomnd">Next of Kin</p>
                    <p className="font-medium" data-id="element-5909" data-mp="3kxtbm8vqy9es">
                      {selectedPatient.nextOfKin} ({selectedPatient.nokContact})
                    </p>
                  </div>
                  <div data-id="element-5910" data-mp="3oxbr2p4stsa8">
                    <p className="text-sm text-gray-500" data-id="element-5911" data-mp="3ty9at65giudu">Allergies</p>
                    <p className="font-medium text-red-600" data-id="element-5912" data-mp="3tweyuhc5yclw">
                      {selectedPatient.allergies}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6" data-id="element-5913" data-mp="31adei2jsv4hl">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-5914" data-mp="368opwhpqtn1x">
                    <Clock className="w-5 h-5 mr-2 text-gray-500" data-id="element-5915" data-mp="3b06stbbbtxpj" /> Visit
                    History
                  </h4>

                  {getPatientEncounters(selectedPatient.id).length > 0 ? <div className="space-y-3" data-id="element-5916" data-mp="2iiubhvprzqfk">
                      {getPatientEncounters(selectedPatient.id).map(enc => <div key={enc.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex justify-between items-center" data-id="element-5917" data-mp="2ayiblvnuzrb4">
                          <div data-id="element-5918" data-mp="20zuaq9o09sun">
                            <p className="font-medium text-gray-900" data-id="element-5919" data-mp="1x026g95na8v3">
                              {new Date(enc.startedAt).toLocaleDateString()} at{' '}
                              {new Date(enc.startedAt).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-5920" data-mp="1n8xw5mqwzc6l">
                              {enc.lastAttendingClinician ? `Seen by ${enc.lastAttendingClinician}` : 'No clinician assigned'}
                            </p>
                          </div>
                          <Badge variant={enc.stage === 'COMPLETED' ? 'success' : 'warning'} data-id="element-5921" data-mp="19cak0cqe4fg3">
                            {enc.stage}
                          </Badge>
                        </div>)}
                    </div> : <p className="text-gray-500 italic" data-id="element-5922" data-mp="krpetylyzziz">
                      No previous visits found.
                    </p>}
                </div>
              </Card>
            </div>}
        </div>}

      {activeTab === 'reports' && <PatientReports data-id="element-5923" data-mp="1icbqfnblygzl" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-5924" data-mp="13ogsnsiqtmfn" />}

      {activeTab === 'maternity' && <Card title="Register Maternity Patient" data-id="element-5925" data-mp="13o75ecp31tdn">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-5926" data-mp="18ms3o6kry2zg">
            <div data-id="element-5927" data-mp="1wd4lc95tne38">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5928" data-mp="1wcmekilj9hh2">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5929" data-mp="215fkvawngxv9">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-5930" data-mp="3ltgjoblmgjb4" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-5931" data-mp="3qnci1f5ss86t" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-5932" data-mp="36w7pzb8uj3g8" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-5933" data-mp="1hj1a1d3vvi1f" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-5934" data-mp="20f9jyu7uch32" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-5935" data-mp="2k4bn6hfdlyqi" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-5936" data-mp="8fn97dsrzgzs" />
                <div className="md:col-span-2" data-id="element-5937" data-mp="r6xy5pexxjp1">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-5938" data-mp="r890rlwnglek" />
                </div>
                <div className="md:col-span-3" data-id="element-5939" data-mp="1a3pcrljjfamr">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-5940" data-mp="34de0i0bucqog" />
                </div>
              </div>
            </div>

            <div data-id="element-5941" data-mp="2ko3pv0qycvx7">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5942" data-mp="2krrepmlgcu5l">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5943" data-mp="2bpi837tb1kxh">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-5944" data-mp="2brbf4o7ty760" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-5945" data-mp="p1onovgvwyco" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-5946" data-mp="17r4rbrnv96xb" />
              </div>
            </div>

            <div data-id="element-5947" data-mp="3iiwhjg2sstwv">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5948" data-mp="3iiedvydt3dex">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5949" data-mp="3eje04ua6r0ys">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-5950" data-mp="6wbwxusgsani" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-5951" data-mp="kqcqma4k77h0" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-5952" data-mp="1yabx4bha19e" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-5953" data-mp="1nj8idh5viqrx" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-5954" data-mp="14qwdamnu5be3" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5955" data-mp="2p67rj703tzu8">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-5956" data-mp="2p5y4nsacw8sr">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};